import { translator } from "../../translation/translator";
import { phpAvatarPaths, phpUrl } from "../../core/php-data-access";
import commonUtil from "../../util/common-utils";
import { Roles } from "../../constants/roles";
import { populateIpGeolocationTooltips } from "../../components/ip-geolocation/ip-geolocation-tooltip";
import { UsersAdminUtils, type UsersAdminLoginHistoryItem, type UsersAdminUser } from "./users-admin-utils";

/** IP lookup link target for the login-history modal's IP column — null when there's no real IP to link. */
function ipLookupUrl(ip: string): string | null {
    const trimmed = ip.trim();
    return trimmed && trimmed !== "-" ? `https://ipinfo.io/${encodeURIComponent(trimmed)}` : null;
}

interface UsersAdminFormLike {
    getStatusEl(): Element | null;
    getCountEl(): Element | null;
    getListEl(): Element | null;
    getHistoryTitleEl(): HTMLElement | null;
    getHistoryStatusEl(): HTMLElement | null;
    getHistorySpinnerEl(): HTMLElement | null;
    getHistoryBodyEl(): HTMLElement | null;
}

export class UsersAdminRender {
    public form: UsersAdminFormLike;

    public roles: typeof UsersAdminUtils.ROLES;

    public constructor(form: UsersAdminFormLike) {
        this.form = form;
        this.roles = UsersAdminUtils.ROLES;
    }

    private hasCustomAvatar(avatar: string): boolean {
        const value = avatar.trim();
        if (!value) {
            return false;
        }

        const defaultFilename = String(phpAvatarPaths()?.defaultFilename || "")
            .trim()
            .replace(/^\/+/, "");
        const defaultUrl = String(phpUrl.avatar() || "").trim();
        const basePath = String(phpUrl.base.avatar() || "")
            .trim()
            .replace(/\/+$/, "");
        const normalizedValue = value.replace(/^\/+/, "");
        const defaultRelativePath =
            defaultFilename && basePath ? `${basePath.replace(/^\/+/, "")}/${defaultFilename}` : "";

        return (
            ![defaultFilename, defaultRelativePath, `/${defaultRelativePath}`, defaultUrl]
                .filter(Boolean)
                .includes(value) && ![defaultFilename, defaultRelativePath].filter(Boolean).includes(normalizedValue)
        );
    }

    // Mirrors AvatarHelper::colorIndexForUserId() (PHP) so this client-rendered
    // table matches the deterministic per-user placeholder color used everywhere
    // else avatars render.
    private avatarColorIndex(userId: string): number {
        const parsed = Number.parseInt(userId, 10);

        return Number.isFinite(parsed) ? Math.abs(parsed) % 10 : 0;
    }

    private setHistoryStatus(message: string, showSpinner: boolean): void {
        const statusEl = this.form.getHistoryStatusEl();
        const spinnerEl = this.form.getHistorySpinnerEl();
        if (spinnerEl instanceof HTMLElement) {
            spinnerEl.hidden = !showSpinner;
            spinnerEl.setAttribute("aria-hidden", showSpinner ? "false" : "true");
        }

        if (!(statusEl instanceof HTMLElement)) {
            return;
        }

        statusEl.hidden = false;

        statusEl.textContent = message;
    }

    public renderMeta(visibleCount: number, totalCount: number): void {
        const statusEl = this.form.getStatusEl();
        if (statusEl instanceof HTMLElement) {
            statusEl.textContent = translator.translate("users_admin_loaded", { count: String(totalCount) });
        }

        const countEl = this.form.getCountEl();
        if (countEl instanceof HTMLElement) {
            countEl.textContent = translator.translate("users_admin_total", { count: String(visibleCount) });
        }
    }

    public renderOptions(values: string[], selectedValue: string, keyPrefix: string): string {
        return values
            .map((value) => {
                const selected = value === selectedValue ? " selected" : "";
                const translateKey = `${keyPrefix === "user_status" ? "status" : keyPrefix}_${value}`;
                const label = translator.translate(translateKey);
                return `<option value="${commonUtil.escapeHtmlAttribute(value)}" data-translate="${commonUtil.escapeHtmlAttribute(translateKey)}"${selected}>${commonUtil.escapeHtml(label)}</option>`;
            })
            .join("");
    }

    public renderList(users: UsersAdminUser[]): void {
        const listEl = this.form.getListEl();
        if (!(listEl instanceof HTMLElement)) {
            return;
        }

        if (!Array.isArray(users) || users.length === 0) {
            listEl.innerHTML = `<div class="users-admin__empty">${commonUtil.escapeHtml(translator.translate("users_admin_empty"))}</div>`;
            return;
        }

        const html = users
            .map((user) => {
                const userId = UsersAdminUtils.toText(user.user_id).trim();
                const nameNotRegisteredLabel = UsersAdminUtils.nameNotRegisteredLabel();
                const nameNotRegisteredTranslateKey = UsersAdminUtils.nameNotRegisteredTranslateKey();
                const profileUrl = commonUtil.resolveUrl(`/users/${encodeURIComponent(userId)}`);
                const avatarRaw = UsersAdminUtils.toText(user.avatar_url).trim();
                const showAvatarPhoto = this.hasCustomAvatar(avatarRaw);
                const avatarUrl = commonUtil.escapeHtmlAttribute(showAvatarPhoto ? phpUrl.avatar(avatarRaw) || "" : "");
                const avatarOnError =
                    "this.style.display='none'; if (this.nextElementSibling instanceof HTMLElement) { this.nextElementSibling.style.display='flex'; }";
                const avatarColorClass = `users-admin__avatar-placeholder--color-${this.avatarColorIndex(userId)}`;
                const displayName = UsersAdminUtils.toText(user.display_name) || UsersAdminUtils.toText(user.nick_name);
                const fullName = UsersAdminUtils.toText(user.full_name);
                const displayNameMarkup = displayName
                    ? commonUtil.escapeHtml(displayName)
                    : `<span data-translate="${commonUtil.escapeHtmlAttribute(nameNotRegisteredTranslateKey)}">${commonUtil.escapeHtml(nameNotRegisteredLabel)}</span>`;
                const fullNameMarkup = fullName
                    ? commonUtil.escapeHtml(fullName)
                    : `<span data-translate="${commonUtil.escapeHtmlAttribute(nameNotRegisteredTranslateKey)}">${commonUtil.escapeHtml(nameNotRegisteredLabel)}</span>`;
                const email = UsersAdminUtils.toText(user.email) || "-";
                const normalizedRole = UsersAdminUtils.normalizeRole(user.role) || "user";
                const normalizedStatus = UsersAdminUtils.normalizeStatus(user.user_status) || "active";
                const normalizedGender = UsersAdminUtils.normalizeGender(user.gender);
                const genderTranslateKey = commonUtil.escapeHtmlAttribute(
                    UsersAdminUtils.genderTranslateKey(normalizedGender),
                );
                const genderIconClass = commonUtil.escapeHtmlAttribute(
                    UsersAdminUtils.genderIconClass(normalizedGender),
                );
                const demographicsBadgeClass = commonUtil.escapeHtmlAttribute(
                    UsersAdminUtils.demographicsBadgeClass(normalizedGender),
                );
                const ageRange = UsersAdminUtils.ageRange(user.date_of_birth);
                const demographicsBadge = `<span class="badge badge-demographics badge--mini ${demographicsBadgeClass}"${genderTranslateKey ? ` data-translate-title="${genderTranslateKey}"` : ""}>${genderIconClass ? `<i class="bi ${genderIconClass}" aria-hidden="true"></i>` : ""}${ageRange ? `<span class="user-demographics-age" data-translate="age_range" data-low="${commonUtil.escapeHtmlAttribute(String(ageRange.low))}" data-high="${commonUtil.escapeHtmlAttribute(String(ageRange.high))}">${commonUtil.escapeHtml(`${ageRange.low}-${ageRange.high}`)}</span>` : `<span class="user-demographics-age" data-translate="age_not_registered">${commonUtil.escapeHtml(translator.translate("age_not_registered"))}</span>`}</span>`;
                const points = commonUtil.escapeHtml(UsersAdminUtils.toText(user.point) || "0");
                const posts = commonUtil.escapeHtml(
                    UsersAdminUtils.toText(user.posts_count) || UsersAdminUtils.toText(user.review_count) || "0",
                );
                const favorites = commonUtil.escapeHtml(UsersAdminUtils.toText(user.favorites_count) || "0");
                const followings = commonUtil.escapeHtml(UsersAdminUtils.toText(user.followings_count) || "0");
                const followers = commonUtil.escapeHtml(UsersAdminUtils.toText(user.followers_count) || "0");
                const updatedAt = commonUtil.escapeHtml(UsersAdminUtils.formatDateTime(user.updated_at));
                const lastLoginAt = commonUtil.escapeHtml(UsersAdminUtils.formatDateTime(user.last_login_at));
                const roleOptions = this.renderOptions(UsersAdminUtils.ROLE_OPTIONS, normalizedRole, "role");
                const userStatusOptions = this.renderOptions(
                    UsersAdminUtils.STATUS_OPTIONS,
                    normalizedStatus,
                    "user_status",
                );
                const userNote = commonUtil.escapeHtml(UsersAdminUtils.toText(user.user_note));
                const userNoteLabel = commonUtil.escapeHtml(translator.translate("users_admin_note"));
                const userNotePlaceholder = commonUtil.escapeHtmlAttribute(
                    translator.translate("users_admin_note_placeholder"),
                );
                const roleLabel = commonUtil.escapeHtml(UsersAdminUtils.roleLabel(normalizedRole));
                const userStatusLabel = commonUtil.escapeHtml(UsersAdminUtils.userStatusLabel(normalizedStatus));
                const rowStateClass = normalizedStatus === "active" ? "" : " users-admin__row--muted";
                const saveLabel = commonUtil.escapeHtmlAttribute(translator.translate("save"));
                const editLabel = commonUtil.escapeHtmlAttribute(translator.translate("users_admin_edit"));
                const historyLabel = commonUtil.escapeHtmlAttribute(translator.translate("users_admin_login_history"));
                const deleteLabel = commonUtil.escapeHtmlAttribute(translator.translate("delete"));
                const pointHistoryUrl = commonUtil.escapeHtmlAttribute(
                    commonUtil.resolveUrl(`/users/${encodeURIComponent(userId)}#point-history`),
                );
                const postsUrl = commonUtil.escapeHtmlAttribute(
                    commonUtil.resolveUrl(`/users/${encodeURIComponent(userId)}#posts`),
                );
                const favoritesUrl = commonUtil.escapeHtmlAttribute(
                    commonUtil.resolveUrl(`/users/${encodeURIComponent(userId)}#favorites`),
                );
                const followingsUrl = commonUtil.escapeHtmlAttribute(
                    commonUtil.resolveUrl(`/users/${encodeURIComponent(userId)}#followings`),
                );
                const followersUrl = commonUtil.escapeHtmlAttribute(
                    commonUtil.resolveUrl(`/users/${encodeURIComponent(userId)}#followers`),
                );

                return `
                    <div class="users-admin__row${rowStateClass}" data-user-id="${commonUtil.escapeHtmlAttribute(userId)}" data-editing="0" data-dirty="0">
                        <div class="users-admin__grid users-admin__grid--body">
                            <div class="users-admin__cell users-admin__cell--no">
                                <span class="users-admin__no-value">${commonUtil.escapeHtml(userId || "-")}</span>
                            </div>
                            <div class="users-admin__cell users-admin__cell--user">
                                <div class="users-admin__user-card">
                                    <a class="users-admin__avatar-link" href="${commonUtil.escapeHtmlAttribute(profileUrl)}" target="_blank" rel="noopener noreferrer" aria-label="${commonUtil.escapeHtmlAttribute(displayName)}">
                                        ${
                                            showAvatarPhoto
                                                ? `<img class="users-admin__avatar" src="${avatarUrl}" alt="${commonUtil.escapeHtmlAttribute(displayName)}" onerror="${avatarOnError}">
                                        <span class="users-admin__avatar-placeholder ${avatarColorClass}" aria-hidden="true" style="display:none;"><i class="bi bi-person-circle"></i></span>`
                                                : `<span class="users-admin__avatar-placeholder ${avatarColorClass}" aria-hidden="true"><i class="bi bi-person-circle"></i></span>`
                                        }
                                    </a>
                                    <div class="users-admin__user-meta">
                                        <a class="users-admin__display-link" href="${commonUtil.escapeHtmlAttribute(profileUrl)}" target="_blank" rel="noopener noreferrer">${displayNameMarkup}</a>
                                        <span class="users-admin__sub-line">${fullNameMarkup}</span>
                                        <div class="users-admin__identity-badges">
                                            <span class="badge badge-role badge-role-${commonUtil.escapeHtmlAttribute(normalizedRole)}">${roleLabel}</span>
                                            <span class="badge badge-status ${commonUtil.escapeHtmlAttribute(normalizedStatus)}">${userStatusLabel}</span>
                                            ${demographicsBadge}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="users-admin__cell users-admin__cell--email">
                                <div class="users-admin__stack">
                                    <span class="users-admin__meta-value users-admin__text-wrap">${commonUtil.escapeHtml(email)}</span>
                                </div>
                            </div>
                            <div class="users-admin__cell users-admin__cell--activity">
                                <div class="users-admin__stats-grid users-admin__field-value">
                                    <a class="users-admin__stats-link" href="${pointHistoryUrl}" target="_blank" rel="noopener noreferrer">${commonUtil.escapeHtml(translator.translate("users_admin_points"))}: ${points}</a>
                                    <a class="users-admin__stats-link" href="${postsUrl}" target="_blank" rel="noopener noreferrer">${commonUtil.escapeHtml(translator.translate("posts"))}: ${posts}</a>
                                    <a class="users-admin__stats-link" href="${favoritesUrl}" target="_blank" rel="noopener noreferrer">${commonUtil.escapeHtml(translator.translate("favorites"))}: ${favorites}</a>
                                    <a class="users-admin__stats-link" href="${followingsUrl}" target="_blank" rel="noopener noreferrer">${commonUtil.escapeHtml(translator.translate("followings"))}: ${followings}</a>
                                    <a class="users-admin__stats-link" href="${followersUrl}" target="_blank" rel="noopener noreferrer">${commonUtil.escapeHtml(translator.translate("followers"))}: ${followers}</a>
                                </div>
                            </div>
                            <div class="users-admin__cell users-admin__cell--dates">
                                <div class="users-admin__stack users-admin__field-value users-admin__stack--dates">
                                    <span>${commonUtil.escapeHtml(translator.translate("users_admin_updated"))}: ${updatedAt}</span>
                                    <span class="users-admin__last-login-row">
                                        <span>${commonUtil.escapeHtml(translator.translate("users_admin_last_login"))}: ${lastLoginAt}</span>
                                        <button type="button" class="btn history users-admin__history-inline-btn" data-role="${this.roles.HISTORY}" data-user-id="${commonUtil.escapeHtmlAttribute(userId)}" data-translate="users_admin_login_history" data-translate-aria-label="users_admin_login_history" aria-label="${historyLabel}">${commonUtil.escapeHtml(translator.translate("users_admin_login_history"))}</button>
                                    </span>
                                </div>
                            </div>
                            <div class="users-admin__cell users-admin__cell--actions">
                                <div class="users-admin__action-group">
                                    <button type="button" class="btn edit users-admin__action-btn users-admin__action-btn--edit" data-role="${this.roles.EDIT}" data-user-id="${commonUtil.escapeHtmlAttribute(userId)}" data-translate-aria-label="users_admin_edit" aria-label="${editLabel}"></button>
                                    <button type="button" class="btn delete users-admin__action-btn users-admin__action-btn--delete" data-role="${this.roles.DELETE}" data-user-id="${commonUtil.escapeHtmlAttribute(userId)}" data-translate-aria-label="delete" aria-label="${deleteLabel}">
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="users-admin__row-bottom">
                            <label class="users-admin__editor-field">
                                <span class="users-admin__field-label">${commonUtil.escapeHtml(translator.translate("users_admin_role"))}</span>
                                <select class="form-select users-admin__editor-select" data-field="role" data-user-id="${commonUtil.escapeHtmlAttribute(userId)}">
                                    ${roleOptions}
                                </select>
                            </label>
                            <label class="users-admin__editor-field">
                                <span class="users-admin__field-label">${commonUtil.escapeHtml(translator.translate("users_admin_status_label"))}</span>
                                <select class="form-select users-admin__editor-select" data-field="user_status" data-user-id="${commonUtil.escapeHtmlAttribute(userId)}">
                                    ${userStatusOptions}
                                </select>
                            </label>
                            <label class="users-admin__editor-field users-admin__editor-field--note">
                                <span class="users-admin__field-label">${userNoteLabel}</span>
                                <textarea class="form-control users-admin__editor-note" data-field="user_note" data-user-id="${commonUtil.escapeHtmlAttribute(userId)}" rows="2" maxlength="1000" placeholder="${userNotePlaceholder}">${userNote}</textarea>
                            </label>
                            <div class="users-admin__editor-actions">
                                <button type="button" class="btn save users-admin__editor-save-btn" data-role="${this.roles.SAVE}" data-user-id="${commonUtil.escapeHtmlAttribute(userId)}" data-translate="save" data-translate-aria-label="save" aria-label="${saveLabel}">${commonUtil.escapeHtml(translator.translate("save"))}</button>
                            </div>
                        </div>
                    </div>
                `;
            })
            .join("");

        listEl.innerHTML = html;
    }

    public renderHistoryTitle(displayName: string): void {
        const titleEl = this.form.getHistoryTitleEl();
        if (!(titleEl instanceof HTMLElement)) {
            return;
        }

        titleEl.textContent = translator.translate("users_admin_login_history_title", {
            name: displayName || "-",
        });
    }

    public renderHistoryLoading(): void {
        const bodyEl = this.form.getHistoryBodyEl();

        this.setHistoryStatus(translator.translate("users_admin_login_history_loading"), true);

        if (bodyEl instanceof HTMLElement) {
            bodyEl.innerHTML = "";
        }
    }

    public renderHistoryError(): void {
        const bodyEl = this.form.getHistoryBodyEl();

        this.setHistoryStatus(translator.translate("users_admin_login_history_load_failed"), false);

        if (bodyEl instanceof HTMLElement) {
            bodyEl.innerHTML = "";
        }
    }

    public renderHistoryList(items: UsersAdminLoginHistoryItem[]): void {
        const statusEl = this.form.getHistoryStatusEl();
        const bodyEl = this.form.getHistoryBodyEl();
        const spinnerEl = this.form.getHistorySpinnerEl();

        if (!(bodyEl instanceof HTMLElement)) {
            return;
        }

        if (!Array.isArray(items) || items.length === 0) {
            this.setHistoryStatus(translator.translate("users_admin_login_history_empty"), false);
            bodyEl.innerHTML = "";
            return;
        }

        if (statusEl instanceof HTMLElement) {
            statusEl.hidden = true;
            statusEl.textContent = "";
        }

        if (spinnerEl instanceof HTMLElement) {
            spinnerEl.hidden = true;
            spinnerEl.setAttribute("aria-hidden", "true");
        }

        bodyEl.innerHTML = items
            .map((item) => {
                const createdAt = commonUtil.escapeHtml(UsersAdminUtils.formatDateTime(item.created_at));
                const method = commonUtil.escapeHtml(UsersAdminUtils.toText(item.login_method) || "-");
                const device = commonUtil.escapeHtml(UsersAdminUtils.toText(item.device_name) || "-");
                const rawIp = UsersAdminUtils.toText(item.ip_address).trim();
                const ipAddress = commonUtil.escapeHtml(rawIp || "-");
                const ipLookup = ipLookupUrl(rawIp);
                const loadingText = commonUtil.escapeHtml(translator.translate("ip_geolocation_loading"));
                // The IP address itself is the link (ISS-09-009); hovering shows IP
                // Geolocation via the shared info-tooltip engine — the nested source
                // span's text is populated async by populateIpGeolocationTooltips()
                // below, keyed off data-ip, after this HTML is in the DOM.
                const ipCell = ipLookup
                    ? `<a href="${commonUtil.escapeHtmlAttribute(ipLookup)}" target="_blank" rel="noopener noreferrer" class="login-history-modal__ip-link" data-info-tooltip-source="1">${ipAddress}<span data-role="${Roles.INFO_TOOLTIP_SOURCE}" data-ip="${commonUtil.escapeHtmlAttribute(rawIp)}" hidden>${loadingText}</span></a>`
                    : ipAddress;
                const failureReason = commonUtil.escapeHtml(
                    UsersAdminUtils.loginFailureReasonLabel(item.failure_reason),
                );
                const success = UsersAdminUtils.toBoolean(item.is_successful);
                const resultLabel = commonUtil.escapeHtml(
                    translator.translate(
                        success ? "users_admin_login_history_success" : "users_admin_login_history_failed",
                    ),
                );
                const resultClass = success ? "is-success" : "is-failed";

                const resultTranslateKey = success
                    ? "users_admin_login_history_success"
                    : "users_admin_login_history_failed";

                return `
                    <article class="login-history-modal__item">
                        <div class="login-history-modal__item-top">
                            <span class="login-history-modal__badge ${resultClass}" data-translate="${resultTranslateKey}">${resultLabel}</span>
                            <span class="login-history-modal__date">${createdAt}</span>
                        </div>
                        <dl class="login-history-modal__grid">
                            <div>
                                <dt data-translate="users_admin_login_history_method">${commonUtil.escapeHtml(translator.translate("users_admin_login_history_method"))}</dt>
                                <dd>${method}</dd>
                            </div>
                            <div>
                                <dt data-translate="users_admin_login_history_device">${commonUtil.escapeHtml(translator.translate("users_admin_login_history_device"))}</dt>
                                <dd>${device}</dd>
                            </div>
                            <div>
                                <dt data-translate="users_admin_login_history_ip">${commonUtil.escapeHtml(translator.translate("users_admin_login_history_ip"))}</dt>
                                <dd>${ipCell}</dd>
                            </div>
                            <div>
                                <dt data-translate="users_admin_login_history_reason">${commonUtil.escapeHtml(translator.translate("users_admin_login_history_reason"))}</dt>
                                <dd>${failureReason}</dd>
                            </div>
                        </dl>
                    </article>
                `;
            })
            .join("");

        populateIpGeolocationTooltips(bodyEl);
    }
}
